import { useQuery } from "@tanstack/react-query";

export type WeatherKind =
  | "sun"
  | "moon"
  | "cloud"
  | "rain"
  | "snow"
  | "wind"
  | "fog";

export interface WeatherResult {
  kind: WeatherKind;
  city: string;
  code: number;
  wind: number;
  isDay: boolean;
}

const STORAGE_KEY = "elg5.weather.v1";
const WIND_KMH_THRESHOLD = 40;

// WMO weather-code buckets (https://open-meteo.com/en/docs — weather_code).
const CLOUD = new Set([1, 2, 3]);
const FOG = new Set([45, 48]);
const SNOW = new Set([71, 73, 75, 77, 85, 86]);
const RAIN = new Set([51, 53, 55, 56, 57, 61, 63, 65, 66, 67, 80, 81, 82, 95, 96, 99]);

/**
 * Pure WMO weather-code + wind + day/night → icon category.
 * Day/night only affects a CLEAR sky (sun by day, moon by night); every other
 * condition looks the same regardless of light.
 */
export function categorize(code: number, windKmh: number, isDay: boolean): WeatherKind {
  if (windKmh >= WIND_KMH_THRESHOLD) return "wind";
  if (code === 0) return isDay ? "sun" : "moon";
  if (CLOUD.has(code)) return "cloud";
  if (FOG.has(code)) return "fog";
  if (SNOW.has(code)) return "snow";
  if (RAIN.has(code)) return "rain";
  return "cloud"; // unknown code → neutral cloud
}

async function fetchWeather(): Promise<WeatherResult> {
  // 1. Free, no-key IP geolocation (client-side only — never touches our backend).
  const geo = await fetch("https://ipwho.is/").then((r) => r.json());
  const lat = Number(geo?.latitude);
  const lon = Number(geo?.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
    throw new Error("geolocation failed");
  }

  // 2. Free, no-key current weather + day/night flag.
  const url =
    `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}` +
    `&current=weather_code,wind_speed_10m,is_day`;
  const w = await fetch(url).then((r) => r.json());
  const code = Number(w?.current?.weather_code ?? -1);
  const wind = Number(w?.current?.wind_speed_10m ?? 0);
  const isDay = Number(w?.current?.is_day ?? 1) === 1;

  const result: WeatherResult = {
    kind: categorize(code, wind, isDay),
    city: typeof geo?.city === "string" ? geo.city : "",
    code,
    wind,
    isDay,
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(result));
  } catch {
    /* ignore quota / private-mode errors */
  }
  return result;
}

function cachedWeather(): WeatherResult | undefined {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as WeatherResult) : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Local weather for the TwoDrive icon — resolved ENTIRELY in the browser via free,
 * keyless public APIs (ipwho.is + Open-Meteo). Never calls our backend, so it adds zero
 * server load. Any failure (offline, rate-limit, CORS) errors the query and callers fall
 * back to the default icon. Cached in localStorage so it shows instantly on reload.
 */
export function useWeather() {
  return useQuery<WeatherResult>({
    queryKey: ["weather"],
    queryFn: fetchWeather,
    initialData: cachedWeather,
    initialDataUpdatedAt: 0, // treat cached value as stale → revalidate in the background
    staleTime: 30 * 60_000,
    gcTime: 60 * 60_000,
    retry: 1,
    refetchOnWindowFocus: false,
  });
}
